// Blocos das telas de Tecnologia, Marketing e Tecnologia + Marketing (#6504). Cada tela
// junta os blocos que são dela; o Felipe vê os das duas áreas.
import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Timer } from 'lucide-react';
import { abrirSolicitacao } from '../../src/lib/abrirSolicitacao';
import { lerMes } from '../../src/lib/visaoGeral/periodo';
import {
  META_CONTRATOS_MIDIA_TRIMESTRE, META_CUSTO_POR_CONTATO, META_DEMANDAS_NO_PRAZO, META_PROJETOS_NO_PRAZO, META_RETRABALHO,
  desempenhoDaMidia, diaEmBrasilia, entregasDoPeriodo, entregasPorSemana, melhoresCriativos, porCampanha, trimestreDe,
  type DemandaConcluida, type Desempenho, type EntregasDoPeriodo, type MetricaDeMidia,
} from '../../src/lib/visaoGeral/tecnologia';
import { CartaoIndicador, PainelDetalhe, plural } from './componentes';
import { metaDaVerba, verbaDeMidia } from './blocosFase3';
import { useMetasMensais } from '../Dashboard/PainelExecutivo/sections/metas/useMetasMensais';

/** Quanto do mês ('AAAA-MM-DD' do 1º e do último dia) já passou: 1 para mês fechado. */
export function fracaoDoMesAte(primeiroDia: string, ultimoDia: string, hoje: Date = new Date()): number {
  const [a, m, d] = ultimoDia.split('-').map(Number);
  const fim = new Date(a, m - 1, d, 23, 59, 59);
  const [a1, m1] = primeiroDia.split('-').map(Number);
  const ini = new Date(a1, m1 - 1, 1);
  if (hoje >= fim) return 1;
  if (hoje < ini) return 0;
  return hoje.getDate() / d;
}
import { useContratosDeMidia, useCronometroAberto, useMidia, type DemandaNaFila } from './useTecnologiaMarketing';

const LISTA = 8;
const pct = (v: number, casas = 0) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: casas })}%`;
const reais = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const reais0 = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const dataCurta = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');
const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
const ROTULO_PRIORIDADE: Record<string, string> = { urgencia: 'urgência', alta: 'alta', media: 'média', baixa: 'baixa' };
const isoDia = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Janelas do mês escolhido (ISO) e o rótulo "OUT/2026". */
export function janelaDoMes(mes: string) {
  const { ano, mes0 } = lerMes(mes)!;
  return {
    inicio: new Date(ano, mes0, 1).toISOString(),
    fim: new Date(ano, mes0 + 1, 1).toISOString(),
    primeiroDia: isoDia(new Date(ano, mes0, 1)),
    ultimoDia: isoDia(new Date(ano, mes0 + 1, 0)),
    rotulo: `${MESES[mes0]}/${ano}`,
    trimestre: trimestreDe(new Date(ano, mes0, 1)),
  };
}

// ─────────────────────────── Tecnologia ───────────────────────────

/** Cronômetro rodando agora, com atalho para a demanda. */
export const BarraCronometro: React.FC<{ pessoaId: string }> = ({ pessoaId }) => {
  const { dados } = useCronometroAberto(pessoaId);
  if (dados === null) return <p className="-mt-4 text-xs text-gray-400">Nenhum cronômetro rodando.</p>;
  const desde = new Date(dados.desde);
  return (
    <button type="button" onClick={() => abrirSolicitacao(String(dados.tarefaId))}
      className="-mt-4 inline-flex items-center gap-2 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 text-xs font-medium px-3 py-1.5 hover:bg-emerald-100">
      <Timer size={14} /> Cronômetro rodando em "{dados.titulo || 'demanda'}" desde {dataCurta(isoDia(desde))} {String(desde.getHours()).padStart(2, '0')}h{String(desde.getMinutes()).padStart(2, '0')}
    </button>
  );
};

export const PainelFilaSemResponsavel: React.FC<{ fila: DemandaNaFila[] | null; titulo?: string; mostrarArea?: boolean }> = ({ fila, titulo = 'Fila sem responsável', mostrarArea }) => (
  <PainelDetalhe titulo={titulo} acao={{ rotulo: 'Central', para: '/tarefas/solicitacoes' }}>
    {fila === null ? (
      <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
    ) : fila.length === 0 ? (
      <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma solicitação esperando alguém pegar.</p>
    ) : (
      <>
        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {fila.slice(0, LISTA).map(d => (
            <li key={d.id}>
              <button type="button" onClick={() => abrirSolicitacao(String(d.id))}
                className="w-full flex items-start gap-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg px-2 -mx-2">
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{d.titulo || 'Sem título'}</span>
                  <span className="block text-xs text-gray-400">
                    aberta em {dataCurta(diaEmBrasilia(d.created_at))}{mostrarArea && d.departamento_solicitacao ? ` · ${d.departamento_solicitacao}` : ''}
                  </span>
                </span>
                <span className={`shrink-0 text-right text-xs font-semibold ${d.prioridade === 'urgencia' ? 'text-red-600 dark:text-red-400' : d.prioridade === 'alta' ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {ROTULO_PRIORIDADE[d.prioridade ?? ''] ?? '—'}
                  {d.vencimento && <span className="block font-normal text-gray-400">prazo {dataCurta(d.vencimento)}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <p className="text-xs text-gray-400 mt-3">
          A mais antiga primeiro.{fila.length > LISTA ? ` Mais ${plural(fila.length - LISTA, 'solicitação', 'solicitações')} na Central.` : ''}
        </p>
      </>
    )}
  </PainelDetalhe>
);

/** Os três indicadores do mês de quem entrega demandas (pessoa ou time). */
export const CartoesEntregas: React.FC<{ e: EntregasDoPeriodo | null; quem: string }> = ({ e, quem }) => (
  <>
    <CartaoIndicador
      titulo="Demandas no prazo"
      valor={e ? (e.taxaPrazo == null ? '—' : pct(e.taxaPrazo, 1)) : null}
      meta={e?.taxaPrazo != null ? { texto: `meta ${pct(META_DEMANDAS_NO_PRAZO)}`, atingido: e.taxaPrazo / META_DEMANDAS_NO_PRAZO } : undefined}
      detalhe={e ? `${e.noPrazo} de ${e.comPrazo} concluídas ${quem} dentro do prazo da prioridade.${e.concluidas > e.comPrazo ? ` ${plural(e.concluidas - e.comPrazo, 'de prioridade baixa (sem prazo) fica', 'de prioridade baixa (sem prazo) ficam')} de fora.` : ''}` : undefined}
      para="/tarefas/solicitacoes"
      origem="Prazo pela prioridade da Central: urgência 1 dia útil, alta 2, média 5 (o dia da abertura não conta; aberta depois das 16h, o dia seguinte também não). A régua vale para todas as demandas, como no plano de bônus."
    />
    <CartaoIndicador
      titulo="Retrabalho"
      valor={e ? (e.taxaRetrabalho == null ? '—' : pct(e.taxaRetrabalho, 1)) : null}
      meta={e?.taxaRetrabalho != null ? { texto: `meta: até ${pct(META_RETRABALHO)}`, atingido: e.taxaRetrabalho <= 0 ? 1 : META_RETRABALHO / e.taxaRetrabalho } : undefined}
      detalhe={e ? `${plural(e.reabertas, 'demanda concluída', 'demandas concluídas')} no mês ${e.reabertas === 1 ? 'já foi reaberta' : 'já foram reabertas'}, de ${e.concluidas}.` : undefined}
      origem="Demanda concluída no mês que voltou de 'concluído' para outro status alguma vez (histórico de status da Central)."
    />
    <CartaoIndicador
      titulo="Projetos no prazo"
      valor={e ? (e.taxaProjetos == null ? '—' : pct(e.taxaProjetos, 1)) : null}
      meta={e?.taxaProjetos != null ? { texto: `meta ${pct(META_PROJETOS_NO_PRAZO)}`, atingido: e.taxaProjetos / META_PROJETOS_NO_PRAZO } : undefined}
      detalhe={e ? (e.projetoComPrazo ? `${e.projetoNoPrazo} de ${e.projetoComPrazo} tarefas de projeto entregues até o vencimento.` : 'Nenhuma tarefa de projeto com vencimento concluída no mês.') : undefined}
      para="/projetos"
      origem="Tarefas de projeto (módulo Projetos) concluídas no mês, contra o vencimento de cada uma."
    />
  </>
);

export const GraficoEntregasPorSemana: React.FC<{ demandas: DemandaConcluida[] | null; titulo?: string }> = ({ demandas, titulo = 'Entregas por semana' }) => {
  const dados = useMemo(() => (demandas ? entregasPorSemana(demandas) : null), [demandas]);
  return (
    <PainelDetalhe titulo={titulo}>
      {!dados ? (
        <div className="h-48 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
      ) : (
        <div className="h-48 -ml-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dados} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="text-gray-200 dark:text-gray-700" />
              <XAxis dataKey="semana" tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9CA3AF' }} tickLine={false} axisLine={false} width={28} />
              <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} formatter={(v: number) => [plural(v, 'demanda', 'demandas'), 'Concluídas']} />
              <Bar dataKey="qtd" fill="#5F6C37" radius={[4, 4, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="text-xs text-gray-400 mt-2">Solicitações concluídas em cada semana do mês (1ª = dias 1 a 7).</p>
    </PainelDetalhe>
  );
};

/** Uma linha por pessoa do time de Tecnologia, com os três indicadores. */
export const TabelaTimeTecnologia: React.FC<{ equipe: { id: string; full_name: string | null }[] | null; demandas: DemandaConcluida[] | null }> = ({ equipe, demandas }) => {
  const linhas = useMemo(() => (equipe && demandas
    ? equipe.map(p => ({ p, e: entregasDoPeriodo(demandas.filter(d => d.responsavel_id === p.id)) })).sort((a, b) => b.e.concluidas - a.e.concluidas)
    : null), [equipe, demandas]);
  const cor = (v: number | null, meta: number, menor = false) => (v == null ? 'text-gray-400' : (menor ? v <= meta : v >= meta) ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400');
  return (
    <PainelDetalhe titulo="Time de Tecnologia no mês">
      {!linhas ? (
        <div className="h-32 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm min-w-[480px]">
            <thead>
              <tr className="text-left text-xs text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="font-medium py-2 px-2">Pessoa</th>
                <th className="font-medium py-2 px-2 text-right">Concluídas</th>
                <th className="font-medium py-2 px-2 text-right">No prazo</th>
                <th className="font-medium py-2 px-2 text-right">Retrabalho</th>
                <th className="font-medium py-2 px-2 text-right">Projetos no prazo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {linhas.map(({ p, e }) => (
                <tr key={p.id}>
                  <td className="py-2 px-2 font-medium text-gray-800 dark:text-gray-100">{p.full_name || 'Sem nome'}</td>
                  <td className="py-2 px-2 text-right">{e.concluidas}</td>
                  <td className={`py-2 px-2 text-right font-semibold ${cor(e.taxaPrazo, META_DEMANDAS_NO_PRAZO)}`}>{e.taxaPrazo == null ? '—' : pct(e.taxaPrazo)}</td>
                  <td className={`py-2 px-2 text-right font-semibold ${cor(e.taxaRetrabalho, META_RETRABALHO, true)}`}>{e.taxaRetrabalho == null ? '—' : pct(e.taxaRetrabalho, 1)}</td>
                  <td className={`py-2 px-2 text-right font-semibold ${cor(e.taxaProjetos, META_PROJETOS_NO_PRAZO)}`}>{e.taxaProjetos == null ? '—' : `${pct(e.taxaProjetos)} (${e.projetoNoPrazo}/${e.projetoComPrazo})`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-400 mt-3">Metas: no prazo 90%, retrabalho até 3%, projetos no prazo 90%. Concluídas pela pessoa responsável pela demanda.</p>
    </PainelDetalhe>
  );
};

// ─────────────────────────── Marketing ───────────────────────────

/** Mídia de ontem (ou do último dia importado), por campanha. */
export function useMidiaDeOntem() {
  const ontem = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 1); return isoDia(d); }, []);
  const midia = useMidia(ontem, ontem, false);
  const total = useMemo(() => (midia.dados ? desempenhoDaMidia(midia.dados.campanhas) : null), [midia.dados]);
  const campanhas = useMemo(() => (midia.dados ? porCampanha(midia.dados.campanhas) : null), [midia.dados]);
  return { ontem, total, campanhas, erro: midia.erro };
}

/** Gasto, contatos e custo por contato por campanha (ontem ou no mês). */
export const PainelCampanhas: React.FC<{ titulo: string; total: Desempenho | null; campanhas: (Desempenho & { campanha: string })[] | null; vazio: string }> = ({ titulo, total, campanhas, vazio }) => (
  <PainelDetalhe titulo={titulo} acao={{ rotulo: 'Campanhas', para: '/marketing/campanhas' }}>
    {!campanhas || !total ? (
      <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
    ) : campanhas.length === 0 ? (
      <p className="text-sm text-gray-500 dark:text-gray-400">{vazio}</p>
    ) : (
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-sm min-w-[420px]">
          <thead>
            <tr className="text-left text-xs text-gray-400 border-b border-gray-100 dark:border-gray-700">
              <th className="font-medium py-2 px-2">Campanha</th>
              <th className="font-medium py-2 px-2 text-right">Gasto</th>
              <th className="font-medium py-2 px-2 text-right">Contatos</th>
              <th className="font-medium py-2 px-2 text-right">Custo/contato</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {campanhas.map(c => (
              <tr key={c.campanha}>
                <td className="py-2 px-2 text-gray-800 dark:text-gray-100 max-w-[220px] truncate" title={c.campanha}>{c.campanha.replace(/^Campanha\s*-\s*/i, '')}</td>
                <td className="py-2 px-2 text-right">{reais0(c.gasto)}</td>
                <td className="py-2 px-2 text-right">{c.contatos}<span className="text-xs text-gray-400"> ({c.leads} leads, {c.conversas} conversas)</span></td>
                <td className={`py-2 px-2 text-right font-semibold ${c.custo == null ? 'text-gray-400' : c.custo <= META_CUSTO_POR_CONTATO ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>{c.custo == null ? '—' : reais(c.custo)}</td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td className="py-2 px-2">Total</td>
              <td className="py-2 px-2 text-right">{reais0(total.gasto)}</td>
              <td className="py-2 px-2 text-right">{total.contatos}</td>
              <td className="py-2 px-2 text-right">{total.custo == null ? '—' : reais(total.custo)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    )}
  </PainelDetalhe>
);

/** Mídia do mês escolhido e do anterior (custo por contato), criativos e contratos de mídia do trimestre. */
export function useMidiaDoMes(mes: string) {
  const j = useMemo(() => janelaDoMes(mes), [mes]);
  const inicioAnterior = useMemo(() => { const { ano, mes0 } = lerMes(mes)!; return isoDia(new Date(ano, mes0 - 1, 1)); }, [mes]);
  const midia = useMidia(inicioAnterior, j.ultimoDia, true);
  const contratos = useContratosDeMidia(new Date(`${j.trimestre.inicio}T00:00:00`).toISOString(), new Date(`${j.trimestre.fimExclusivo}T00:00:00`).toISOString());
  const doMes = (l: MetricaDeMidia) => l.date >= j.primeiroDia && l.date <= j.ultimoDia;
  const atual = useMemo(() => (midia.dados ? desempenhoDaMidia(midia.dados.campanhas.filter(doMes)) : null), [midia.dados, j]);
  const anterior = useMemo(() => (midia.dados ? desempenhoDaMidia(midia.dados.campanhas.filter(l => !doMes(l))) : null), [midia.dados, j]);
  const campanhasDoMes = useMemo(() => (midia.dados ? porCampanha(midia.dados.campanhas.filter(doMes)) : null), [midia.dados, j]);
  const criativos = useMemo(() => (midia.dados ? melhoresCriativos(midia.dados.anuncios.filter(doMes)) : null), [midia.dados, j]);
  const contratosNoMes = contratos.dados ? contratos.dados.filter(c => c.aprovadaEm >= j.inicio && c.aprovadaEm < j.fim) : null;
  return { j, atual, anterior, campanhasDoMes, criativos, contratosTrimestre: contratos.dados, contratosNoMes };
}

export const CartoesMidiaDoMes: React.FC<{ m: ReturnType<typeof useMidiaDoMes>; qualidade?: React.ReactNode }> = ({ m, qualidade }) => {
  const mesAnterior = (() => { const { ano, mes0 } = lerMes(`${m.j.primeiroDia.slice(0, 7)}`)!; const d = new Date(ano, mes0 - 1, 1); return `${MESES[d.getMonth()]}/${d.getFullYear()}`; })();
  const kwh = m.contratosNoMes?.reduce((s, c) => s + c.kwh, 0) ?? 0;
  // Verba de mídia do mês (#6509), definida na aba Metas do Painel Executivo.
  const { metas } = useMetasMensais();
  const verba = m.atual ? verbaDeMidia(metas, m.j.primeiroDia.slice(0, 7), m.atual.gasto, fracaoDoMesAte(m.j.primeiroDia, m.j.ultimoDia)) : null;
  return (
    <>
      <CartaoIndicador
        titulo="Custo por contato"
        valor={m.atual ? (m.atual.custo == null ? '—' : reais(m.atual.custo)) : null}
        meta={m.atual?.custo != null ? { texto: `meta: até ${reais(META_CUSTO_POR_CONTATO)}`, atingido: m.atual.custo <= 0 ? 1 : META_CUSTO_POR_CONTATO / m.atual.custo } : undefined}
        detalhe={m.atual ? `${reais0(m.atual.gasto)} investidos ÷ ${m.atual.contatos.toLocaleString('pt-BR')} contatos (${m.atual.leads} leads e ${m.atual.conversas} conversas).${m.anterior?.custo != null ? ` ${mesAnterior}: ${reais(m.anterior.custo)}.` : ''}` : undefined}
        para="/marketing/campanhas"
        origem="Investimento no Meta Ads ÷ (leads de formulário + conversas iniciadas no WhatsApp), no mês. É o custo por contato do plano de bônus."
      />
      {qualidade}
      <CartaoIndicador
        titulo={`Contratos de mídia no ${m.j.trimestre.rotulo}`}
        valor={m.contratosTrimestre ? String(m.contratosTrimestre.length) : null}
        meta={m.contratosTrimestre ? { texto: `meta ${META_CONTRATOS_MIDIA_TRIMESTRE} no trimestre`, atingido: m.contratosTrimestre.length / META_CONTRATOS_MIDIA_TRIMESTRE } : undefined}
        detalhe={m.contratosNoMes ? `${plural(m.contratosNoMes.length, 'aprovado', 'aprovados')} em ${m.j.rotulo}${kwh ? ` (${Math.round(kwh).toLocaleString('pt-BR')} kWh)` : ''}.` : undefined}
        para="/crm"
        origem="Negociações com origem 'Mídia paga' aprovadas na validação de dados no trimestre (data da aprovação)."
      />
      <CartaoIndicador
        titulo="Investimento contra a verba"
        valor={m.atual ? reais0(m.atual.gasto) : null}
        meta={verba ? metaDaVerba(verba) : undefined}
        nota={verba && !verba.orcamento ? 'Verba do mês não definida: a diretoria define na aba Metas do Painel Executivo.' : verba?.estourou ? `No ritmo de até hoje, passa da verba em ${reais0(verba.projecao - (verba.orcamento ?? 0))}.` : undefined}
        detalhe={m.atual ? `${m.atual.contatos.toLocaleString('pt-BR')} contatos em ${m.j.rotulo}.${m.anterior ? ` ${mesAnterior}: ${reais0(m.anterior.gasto)} e ${m.anterior.contatos.toLocaleString('pt-BR')} contatos.` : ''}` : undefined}
        para="/marketing/campanhas"
        origem="Gasto no Meta Ads importado para o VigorHub, contra a verba de mídia do mês (aba Metas do Painel Executivo, indicador Investimento em mídia). Sem verba nova no mês, vale a última definida."
      />
    </>
  );
};

export const PainelMelhoresCriativos: React.FC<{ criativos: ReturnType<typeof melhoresCriativos> | null; rotuloMes: string }> = ({ criativos, rotuloMes }) => (
  <PainelDetalhe titulo="Melhores criativos" acao={{ rotulo: 'Criativos', para: '/marketing/criativos' }}>
    {!criativos ? (
      <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
    ) : criativos.length === 0 ? (
      <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum anúncio com 10 ou mais contatos em {rotuloMes}.</p>
    ) : (
      <ol className="space-y-2">
        {criativos.map((c, i) => (
          <li key={c.adId} className="flex items-start gap-3">
            <span className="shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate" title={c.nome}>{c.nome}</span>
              <span className="block text-xs text-gray-400 truncate">{c.campanha?.replace(/^Campanha\s*-\s*/i, '') ?? '—'} · {c.contatos} contatos · {reais0(c.gasto)}</span>
            </span>
            <span className="shrink-0 text-sm font-semibold text-emerald-700 dark:text-emerald-400">{reais(c.custo!)}</span>
          </li>
        ))}
      </ol>
    )}
    <p className="text-xs text-gray-400 mt-3">Os 3 anúncios com menor custo por contato em {rotuloMes}, entre os que trouxeram 10 contatos ou mais.</p>
  </PainelDetalhe>
);
